import { Injectable } from '@angular/core';
import { MmAlertSize, MmAlertType, MmDialogService } from '@metromarkets/components';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, first, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  ChallengeActionType,
  ChallengeDetailsRequestModel,
  ChallengeResponse,
  OrderPayment,
  PaymentResponse,
  PaymentResultViewTypes,
  PaymentRequest,
  ServicePaymentMethodTypes
} from '~app/modules/checkout/models';
import {
  CheckoutService,
  Payment3dsService,
  PaymentResultViewService,
  StepperService,
} from '~app/modules/checkout/services';
import { ClearAddresses, State } from '~app/modules/checkout/store/addresses';

import { ErrorResponse } from '~shared/models';
import {
  CheckoutErrorsApplicationEvent,
  CheckoutFunnelTrackingApplicationEventModel,
} from '~shared/modules/application-event/models';
import { PaymentFailureApplicationEvent } from '~shared/modules/application-event/models/payment-failure-application-event.model';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { BadRequestHttpError, GeneralHttpError } from '~shared/modules/error-handler';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';
import { GetCartTotalQuantity } from '~shared/modules/shopping-cart-global/store';

import { AccountInfoService } from '~app/core/modules/identity/services/account-info.service';
import { NotificationService } from '~shared/services/notification.service';
import { Adyen3DSDialogComponent } from '../../components/adyen-3ds-dialog/adyen-3ds-dialog.component';
import {
  ADDRESS_FIELDS,
  ADDRESS_TYPES,
  ADYEN_3DS_MODAL_OPTIONS_CHALLENGE_FLOW,
  PaymentErrorMessages,
  PaymentMethodOptions,
} from '../../constants';
import { CheckoutCommonUtil } from '../../utils/checkout-common-util';
import { paymentSelectors } from '../selectors';
import {
  Adyen3DSDetails,
  InitAdyen3DSFlow,
  InitPayment,
  InitPaymentFailure,
  InitPaymentSuccess,
  InitPaypalFlow,
  InitSofortFlow,
  MakePayment,
  MakePaymentFailure,
  MakePaymentOtherFailure,
  MakePaymentSuccess,
  PaymentActionTypes,
  SofortDetails,
} from './payment.actions';
import { RavelinService } from '~shared/modules/ravelin/services/ravelin.service';
import { CriteoService } from '~app/modules/checkout/services/criteo.service';

const creditCard3DS1RefusalReason = '3D Not Authenticated';
@Injectable()
export class PaymentEffects {
  @Effect()
  initPayment$: Observable<Action> = this.actions$.pipe(
    ofType(PaymentActionTypes.INIT_PAYMENT),
    map((action: InitPayment) => action.payload),
    switchMap(({ address }) =>
      this.checkoutService.initPayment(address).pipe(
        map((paymentWidgetConfig) => {
          this.serverLogger.info('Checkout - FE - Get Widget Data Response', {
            ...paymentWidgetConfig,
          });

          return new InitPaymentSuccess({
            paymentWidgetConfig: paymentWidgetConfig.paymentMethods,
            lastPaymentMethodUsed: paymentWidgetConfig.lastPaymentMethodUsed,
          });
        }),
        catchError((error) => {
          if (!(error instanceof GeneralHttpError || error instanceof BadRequestHttpError)) {
            // TODO: Handle Client Error (Angular Error, ReferenceError...)
            return of(new InitPaymentFailure({ error }));
          }

          this.serverLogger.error('Checkout - FE - Get Widget Data Error', {
            ...error,
          });
          this.showErrorMessage(error);

          return of(new InitPaymentFailure({ error }));
        })
      )
    )
  );

  @Effect()
  makePayment$: any = this.actions$.pipe(
    ofType(PaymentActionTypes.MAKE_PAYMENT),
    map((action: MakePayment) => action.payload),
    switchMap(({ payment, isAssistedSales }) =>
      this.checkoutService.makePayment(payment, isAssistedSales).pipe(
        map((paymentResponse: OrderPayment) => {
          CheckoutCommonUtil.savePaymentRequestToLocalStorage(payment, paymentResponse.orderNumber);
          return this.handlePayment(paymentResponse, 'Make Payment');
        }),
        catchError((error) => {
          this.trackPaymentError(error.detail, payment);
          if (!(error instanceof GeneralHttpError || error instanceof BadRequestHttpError)) {
            return of(new MakePaymentOtherFailure({ error }));
          }

          this.serverLogger.error('Checkout - FE - Make Payment error', {
            ...error,
          });
          const isImpersonateConflict =
            isAssistedSales && error.status === AccountInfoService.HTTPImpersonateConflictCode;
          if (!isImpersonateConflict) {
            this.showErrorMessage(error);
          }

          return of(new MakePaymentOtherFailure({ error }));
        })
      )
    )
  );

  @Effect()
  makePaymentSuccess$: Observable<Action> = this.actions$.pipe(
    ofType(PaymentActionTypes.MAKE_PAYMENT_SUCCESS),
    map((action: MakePaymentSuccess) => {
      this.criteoService.sendTrackTransactionEvent();
      return action.payload.payment.response;
    }),
    tap(this.definePaymentResultView.bind(this)),
    tap(() => this.store.dispatch(new ClearAddresses())),
    map(() => new GetCartTotalQuantity())
  );

  @Effect({ dispatch: false })
  init3DSFlow$ = this.actions$.pipe(
    ofType(PaymentActionTypes.INIT_ADYEN_3DS_FLOW, PaymentActionTypes.INIT_SOFORT_FLOW),
    map((action: InitAdyen3DSFlow | InitSofortFlow) => action.payload),
    tap(this.track3DSType.bind(this)),
    withLatestFrom(this.payment3DSService.waitForFingerprintResults$),
    filter(([, isEnabled]) => !isEnabled),
    mergeMap(([payload]) =>
      this.store.pipe(
        first(),
        select(paymentSelectors.getPaymentWidgetCreditCardConfig),
        map((adyenConfig) => [payload.challengeResponse.action, payload.orderNumber, adyenConfig])
      )
    ),
    tap(this.open3DSDialog.bind(this))
  );

  @Effect()
  challengeDetails$: Observable<Action> = this.actions$.pipe(
    ofType(PaymentActionTypes.ADYEN_3DS_DETAILS, PaymentActionTypes.SOFORT_DETAILS, PaymentActionTypes.PAYPAL_DETAILS),
    map((action: Adyen3DSDetails | SofortDetails) => action.payload),
    mergeMap((payload) =>
      this.store.pipe(
        select(paymentSelectors.getPaymentRequest),
        map((payment) => (this.getPaymentDetailsPayload(payload, payment)))
      )
    ),
    switchMap(({ payload }) =>
      this.checkoutService.paymentDetails(this.updatePaymentDetailPayloadForRavelin(payload)).pipe(
        map((paymentResponse: OrderPayment) => this.handlePayment(paymentResponse, 'Payment Details')),
        catchError((error) => {
          const is3DSLoginFailure = error?.sourceError?.error?.refusalReason === creditCard3DS1RefusalReason;
          const message =
            PaymentErrorMessages[is3DSLoginFailure ? '3DS1_WRONG_LOG_IN' : 'PAYMENT_FAILED_GENERIC_MESSAGE'];
          this.serverLogger.error('Checkout - FE - Payment Details error', {
            ...error,
          });
          this.notificationService.notify({
            message,
            type: MmAlertType.error,
            size: MmAlertSize.large,
          });

          this.applicationEventsService.publish(new CheckoutErrorsApplicationEvent(error));
          const { paymentRequest } = CheckoutCommonUtil.getPaymentRequestFromLocalStorage() || {};
          this.stepperService.prevStep();
          return of(new MakePaymentOtherFailure({ error: error?.sourceError?.error ?? error, paymentRequest }));
        })
      )
    )
  );

  private getPaymentDetailsPayload(payload: ChallengeDetailsRequestModel, payment: PaymentRequest): { payload: ChallengeDetailsRequestModel, payment: PaymentRequest } {
    try {
      if (!!payload?.details?.redirectResult) {
        return { payment, payload: { ...payload, details: { redirectResult: payload?.details?.redirectResult } } }
      }
      return { payload, payment }
    } catch (e) {
      return { payload, payment }
    }
  }

  private updatePaymentDetailPayloadForRavelin(payload: ChallengeDetailsRequestModel): ChallengeDetailsRequestModel {
    try {
      if (typeof payload !== 'object') return payload;
      return {
        ...payload,
        ...this.ravelinService.getRavelinObject(),
      };
    } catch (e) {
      return payload;
    }
  }

  private handleFailedRedirection(errorMessage: string): void {
    const failedRedirectionsSteps: Record<string, number> = {
      'CHECKOUT.ORDER.VALIDATION.FREIGHT_FORWARD_PHONE_REQUIRED': 0,
    };
    if (errorMessage in failedRedirectionsSteps) {
      this.stepperService.goToStep(failedRedirectionsSteps[errorMessage]);
    }
  }

  private showErrorMessage(error: ErrorResponse): void {
    let typeParam: string;
    let fieldParam: string;
    let errorMessage = error.detail || error.title || error.sourceError.error?.message;
    let errorTitle: string;
    let addressType: string;
    let addressField: string;

    if (error instanceof BadRequestHttpError && Object.keys(error.invalidParams).length > 0) {
      errorMessage = Object.values(error.invalidParams)[0].reason;
      [addressType, addressField] = Object.keys(error.invalidParams)[0].split('.');
    } else if (error instanceof BadRequestHttpError && error.sourceError.error?.errors?.length > 0) {
      errorMessage = error.sourceError.error.errors[0].message;
      [addressType, addressField] = error.sourceError.error.errors[0].fields[0]?.name?.split('.');
    }

    this.handleFailedRedirection(errorMessage);

    if (addressType && addressField) {
      errorTitle = 'CHECKOUT.ERROR.INVALID_ADDRESS_DATA';
      this.translateService
        .get([ADDRESS_TYPES[addressType], ADDRESS_FIELDS[addressField]])
        .pipe(take(1))
        .subscribe((translations) => {
          typeParam = translations[ADDRESS_TYPES[addressType]];
          fieldParam = translations[ADDRESS_FIELDS[addressField]];
        });
    }

    if (!errorTitle && !errorMessage) {
      return;
    }

    this.notificationService.notify(
      {
        title: errorTitle,
        message: errorMessage,
        type: MmAlertType.error,
        size: MmAlertSize.large,
      },
      { typeParam, fieldParam }
    );
  }

  private open3DSDialog([action, orderNumber, adyenConfig]) {
    this.dialog.open(Adyen3DSDialogComponent, {
      data: {
        adyenResponseAction: action,
        adyenConfig,
        orderNumber,
      },
      ...ADYEN_3DS_MODAL_OPTIONS_CHALLENGE_FLOW,
    });
  }

  private handlePayment(paymentResponse: OrderPayment, endpoint: string) {
    const { paymentRequest } = CheckoutCommonUtil.getPaymentRequestFromLocalStorage();

    this.serverLogger.info(`Checkout - FE - ${endpoint} Response`, {
      ...paymentResponse,
    });

    if (paymentResponse.response && !paymentResponse.verified && !!paymentResponse.response.authenticationChallenge) {
      return this.determineChallengeFlow(paymentResponse);
    } else {
      CheckoutCommonUtil.clearPaymentRequestFromLocalStorage();
      if (paymentResponse.verified) {
        this.payment3DSService.setWaitForFingerprintResults(false);
        this.saveOrderNumber(paymentResponse);
        return new MakePaymentSuccess({ payment: paymentResponse, request: paymentRequest });
      } else {
        this.trackPaymentError(paymentResponse.detail, paymentRequest);
        return new MakePaymentFailure({ error: paymentResponse, request: paymentRequest });
      }
    }
  }

  private track3DSType(payload: {
    challengeResponse: ChallengeResponse;
    orderNumber: string;
    challengeType: string;
  }): void {
    const { action } = payload.challengeResponse;
    const paymentMethod = payload?.challengeResponse?.action?.paymentMethodType;
    let label = '';
    if (
      [ChallengeActionType.Challenge, ChallengeActionType.Redirect].includes(action.type) &&
      paymentMethod !== PaymentMethodOptions.DirectEbanking
    ) {
      switch (action.type) {
        case ChallengeActionType.Challenge:
          label = '3DS 2 Iframe';
          break;
        case ChallengeActionType.Redirect:
          label = '3DS 1 Redirect';
          break;
      }
      this.applicationEventsService.publish(new CheckoutFunnelTrackingApplicationEventModel(label));
    } else if (paymentMethod === PaymentMethodOptions.DirectEbanking) {
      if (action.type === ChallengeActionType.Redirect) {
        label = 'Sofort Redirect';
      }

      this.applicationEventsService.publish(new CheckoutFunnelTrackingApplicationEventModel(label));
    }
  }

  private trackPaymentError(error: any, payment: any): void {
    this.applicationEventsService.publish(new PaymentFailureApplicationEvent(error, payment));
  }

  constructor(
    private actions$: Actions,
    private applicationEventsService: ApplicationEventService,
    private checkoutService: CheckoutService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private paymentResultViewService: PaymentResultViewService,
    private payment3DSService: Payment3dsService,
    private store: Store<State>,
    private stepperService: StepperService,
    private serverLogger: ServerLoggerService,
    public dialog: MmDialogService,
    private ravelinService: RavelinService,
    private criteoService: CriteoService
  ) { }

  private definePaymentResultView({ orderLines }: PaymentResponse): void {
    const isPartiallySuccess = orderLines.length > 0 && orderLines.some((line) => !line.success);

    if (isPartiallySuccess) {
      this.paymentResultViewService.view = PaymentResultViewTypes.PartiallySuccess;
    } else {
      this.paymentResultViewService.view = PaymentResultViewTypes.Success;
    }
  }

  private determineChallengeFlow(paymentResponse: OrderPayment) {
    const challengeResponse = paymentResponse.response.authenticationChallenge;
    const orderNumber = paymentResponse.orderNumber;
    const challengeType = paymentResponse.challengeType;
    const action = challengeResponse ? challengeResponse.action : null;
    const paymentMethod = challengeResponse.action.paymentMethodType;
    switch (action.type) {
      case ChallengeActionType.Challenge:
      case ChallengeActionType.Fingerprint:
        return new InitAdyen3DSFlow({
          challengeResponse,
          orderNumber,
          challengeType,
        });

      case ChallengeActionType.Redirect:
        if (paymentMethod === PaymentMethodOptions.DirectEbanking) {
          return new InitSofortFlow({
            challengeResponse,
            orderNumber,
            challengeType,
          });
        } else {
          return new InitAdyen3DSFlow({
            challengeResponse,
            orderNumber,
            challengeType,
          });
        }

      case ChallengeActionType.Paypal:
        return new InitPaypalFlow({
          challengeResponse,
          orderNumber,
        });

      default:
        return null;
    }
  }

  private saveOrderNumber(paymentResponse: OrderPayment) {
    if (paymentResponse?.orderNumber) {
      CheckoutCommonUtil.saveTmpOrderNumberToLocalStorage(paymentResponse.orderNumber);
    }
  }
}
